/**
 * Quiet, photographic covers drawn from the proportion and typography of a portrait annual.
 * Keep the artwork in the existing editable DOM; these rules intentionally follow formatStyles.
 */
export const quietCoverStyles = `
body[data-format] .page.storybook,
body[data-format] .page.growth{
  --quiet-paper:#f4f0e6;
  --quiet-ink:#403d33;
  --quiet-muted:#8b8171;
  --quiet-rule:rgba(88,77,57,.3);
  background:var(--quiet-paper);
  color:var(--quiet-ink);
  padding:0;
  font-family:'Noto Serif JP Variable',serif;
  -webkit-print-color-adjust:exact;
  print-color-adjust:exact;
}
body[data-format] :is(.page.storybook,.page.growth) .photo{
  border-radius:0;
  box-shadow:none;
}
body[data-format] :is(.page.storybook,.page.growth) .empty-photo{
  width:100%;height:100%;
  background:#dcded3;
  color:#747b68;
  font-family:'Noto Serif JP Variable',serif;
  font-size:calc(var(--page-w) * .016);
  letter-spacing:.14em;
}
body[data-format] :is(.page.storybook,.page.growth)>.brand{
  right:6.5%;bottom:2.3%;
  color:var(--quiet-muted);
  font-family:'Noto Sans JP Variable',sans-serif;
  font-size:calc(var(--page-w) * .008);
  letter-spacing:.15em;
}
body[data-format] :is(.page.storybook,.page.growth)>.brand b{color:var(--quiet-ink);font-weight:500}

/* An ivory title leaf beside the portrait plate, with an open, horizontal story below. */
body[data-format] .page.storybook{display:block}
body[data-format] .page.storybook .story-orb{display:none}
body[data-format] .page.storybook::before{
  content:'COLLECTED MOMENTS';
  position:absolute;left:6.5%;right:6.5%;bottom:2.3%;
  border-top:1px solid var(--quiet-rule);
  padding-top:calc(var(--page-w) * .011);
  color:var(--quiet-muted);
  font-family:'Bodoni Moda',serif;
  font-size:calc(var(--page-w) * .0085);
  letter-spacing:.2em;
}
body[data-format] .page.storybook>.eyebrow{
  position:absolute;left:6.5%;right:6.5%;top:4.1%;
  display:flex;justify-content:space-between;align-items:center;
  margin:0;padding-bottom:calc(var(--page-w) * .017);
  border-bottom:1px solid var(--quiet-rule);
  font-family:'Noto Sans JP Variable',sans-serif;
  font-size:calc(var(--page-w) * .011);font-weight:400;letter-spacing:.14em;
}
body[data-format] .page.storybook>h1{
  position:absolute;top:13%;right:6.5%;
  width:23%;height:50%;max-width:none;margin:0;
  writing-mode:vertical-rl;text-orientation:mixed;
  font-family:'Noto Serif JP Variable',serif;
  font-size:calc(var(--page-w) * .041 * var(--quiet-title-fit,1));
  font-weight:400;line-height:1.65;letter-spacing:.16em;
  text-wrap:wrap;line-break:strict;overflow-wrap:anywhere;
}
body[data-format] .page.storybook .story-photo{
  position:absolute;left:6.5%;top:12.6%;
  width:58%;height:59.5%;margin:0;
  border-radius:0;box-shadow:none;overflow:hidden;
  background:#dcded3;
}
body[data-format] .page.storybook:has(.storybook-thumbs) .story-photo{height:48.5%}
body[data-format] .page.storybook .storybook-thumbs{
  position:absolute;left:6.5%;top:63.1%;
  width:58%;height:9%;
  display:grid;grid-template-columns:repeat(3,minmax(0,1fr));
  gap:calc(var(--page-w) * .012);
}
body[data-format] .page.storybook .storybook-thumbs .secondary-photo{
  width:100%;height:100%;min-height:0;aspect-ratio:auto;
  border:0;border-radius:0;box-shadow:none;
}
body[data-format] .page.storybook .story-copy{
  position:absolute;left:6.5%;right:6.5%;top:74.2%;bottom:6.1%;
  width:auto;max-height:none;margin:0;
  font-family:'Noto Serif JP Variable',serif;
}
body[data-format] .page.storybook .story-copy h2{
  margin:0 0 calc(var(--page-w) * .009);
  font-size:calc(var(--page-w) * .021);font-weight:500;
  line-height:1.6;letter-spacing:.055em;
}
body[data-format] .page.storybook .story-copy p{
  margin:0;font-size:calc(var(--page-w) * .0115);line-height:1.95;
  letter-spacing:.025em;text-align:justify;overflow-wrap:anywhere;
}
body[data-format] .page.storybook .story-copy blockquote{
  margin:calc(var(--page-w) * .013) 0 0;
  padding:0 0 0 calc(var(--page-w) * .014);
  border-left:1px solid var(--quiet-rule);
  color:#726a5c;font-size:calc(var(--page-w) * .0095);line-height:1.9;
  letter-spacing:.03em;
}

/* The shorter social format keeps the leaf and the story in the same considered proportions. */
body[data-format='sns_4x5_jpeg'] .page.storybook>h1{
  top:12.5%;height:47%;font-size:calc(var(--page-w) * .038 * var(--quiet-title-fit,1));
}
body[data-format='sns_4x5_jpeg'] .page.storybook .story-photo{top:12%;height:56%}
body[data-format='sns_4x5_jpeg'] .page.storybook:has(.storybook-thumbs) .story-photo{height:45%}
body[data-format='sns_4x5_jpeg'] .page.storybook .storybook-thumbs{top:59%;height:9%}
body[data-format='sns_4x5_jpeg'] .page.storybook .story-copy{top:72%;bottom:6.5%}
body[data-format='sns_4x5_jpeg'] .page.storybook .story-copy p{font-size:calc(var(--page-w) * .0105);line-height:1.9}

/* A tall plate lets a story-format portrait breathe without changing the typography scale. */
body[data-format='story_9x16_jpeg'] .page.storybook>h1{top:12%;height:50%;font-size:calc(var(--page-w) * .05 * var(--quiet-title-fit,1))}
body[data-format='story_9x16_jpeg'] .page.storybook .story-photo{top:11.6%;height:60.5%}
body[data-format='story_9x16_jpeg'] .page.storybook:has(.storybook-thumbs) .story-photo{height:49.5%}
body[data-format='story_9x16_jpeg'] .page.storybook .story-copy p{font-size:calc(var(--page-w) * .0125);line-height:2}
body[data-format='story_9x16_jpeg'] .page.storybook .story-copy h2{font-size:calc(var(--page-w) * .024)}
body[data-format='story_9x16_jpeg'] .page.storybook .story-copy blockquote{font-size:calc(var(--page-w) * .011)}

/* Landscape becomes a photographic spread: one picture page and one reading page. */
body[data-format='two_l_jpeg'] .page.storybook{display:block;padding:0}
body[data-format='two_l_jpeg'] .page.storybook::before{left:6%;right:6%;bottom:3%}
body[data-format='two_l_jpeg'] .page.storybook>.eyebrow{
  left:55%;right:6%;top:7%;font-size:calc(var(--page-w) * .0075);
  padding-bottom:calc(var(--page-w) * .011);
}
body[data-format='two_l_jpeg'] .page.storybook>h1{
  left:55%;right:6%;top:16%;width:auto;height:23%;margin:0;max-width:none;
  writing-mode:horizontal-tb;
  font-size:calc(var(--page-w) * .027 * var(--quiet-title-fit,1));
  line-height:1.6;letter-spacing:.08em;
}
body[data-format='two_l_jpeg'] .page.storybook .story-photo{
  left:6%;top:7%;width:43%;height:81%;margin:0;border-radius:0;
}
body[data-format='two_l_jpeg'] .page.storybook:has(.storybook-thumbs) .story-photo{height:60%}
body[data-format='two_l_jpeg'] .page.storybook .storybook-thumbs{left:6%;top:70%;width:43%;height:18%}
body[data-format='two_l_jpeg'] .page.storybook .story-copy{
  left:55%;right:6%;top:43%;bottom:11%;width:auto;max-height:none;margin:0;
  padding-top:calc(var(--page-w) * .014);border-top:1px solid var(--quiet-rule);
}
body[data-format='two_l_jpeg'] .page.storybook .story-copy h2{font-size:calc(var(--page-w) * .015)}
body[data-format='two_l_jpeg'] .page.storybook .story-copy p{font-size:calc(var(--page-w) * .0082);line-height:1.9}
body[data-format='two_l_jpeg'] .page.storybook .story-copy blockquote{font-size:calc(var(--page-w) * .0075);line-height:1.85}
body[data-format='two_l_jpeg'] .page.storybook>.brand{bottom:3%;right:6%;font-size:calc(var(--page-w) * .006)}

/* A botanical annual: a large portrait, serif masthead, and a restrained numbered index. */
body[data-format] .page.growth{
  --quiet-paper:#f3f3eb;--quiet-ink:#324238;--quiet-muted:#7c8575;
  --quiet-rule:rgba(66,85,65,.28);
}
body[data-format] .page.growth::before{
  content:'LIFE, GROWING.';
  position:absolute;left:6.5%;right:6.5%;bottom:2.3%;
  border-top:1px solid var(--quiet-rule);padding-top:calc(var(--page-w) * .011);
  font-family:'Bodoni Moda',serif;font-size:calc(var(--page-w) * .009);
  color:var(--quiet-muted);letter-spacing:.18em;
}
body[data-format] .page.growth>header{
  position:absolute;left:6.5%;right:6.5%;top:4.3%;height:17.8%;
  display:flex;align-items:start;justify-content:space-between;gap:4%;
  border-bottom:1px solid var(--quiet-rule);padding:0;
}
body[data-format] .page.growth>header>div{width:100%;min-width:0}
body[data-format] .page.growth>header .eyebrow{
  margin:0 0 calc(var(--page-w) * .022);
  font-family:'Noto Sans JP Variable',sans-serif;
  font-size:calc(var(--page-w) * .0105);line-height:1.8;
  font-weight:400;letter-spacing:.17em;
}
body[data-format] .page.growth>header h1{
  width:100%;max-height:calc(var(--page-h) * .107);margin:0;
  font-family:'Noto Serif JP Variable',serif;
  font-size:calc(var(--page-w) * .041 * var(--quiet-title-fit,1));
  font-weight:400;line-height:1.55;letter-spacing:.065em;
  overflow-wrap:anywhere;text-wrap:balance;
}
body[data-format] .page.growth>header time{
  position:absolute;right:0;bottom:calc(var(--page-w) * .011);
  font-family:'Bodoni Moda',serif;font-size:calc(var(--page-w) * .010);
  color:var(--quiet-muted);letter-spacing:.06em;white-space:nowrap;
}
body[data-format] .page.growth .growth-grid{
  position:absolute;left:6.5%;right:6.5%;top:26%;bottom:8%;
  display:grid;grid-template-columns:43% minmax(0,1fr);gap:7%;
  height:auto;margin:0;
}
body[data-format] .page.growth .growth-visual{position:relative;min-width:0;min-height:0}
body[data-format] .page.growth .growth-photo{
  width:100%;height:74%;overflow:hidden;border-radius:0;box-shadow:none;
  background:#dcded3;
}
body[data-format] .page.growth .growth-thumbs{
  display:grid;grid-template-columns:repeat(3,minmax(0,1fr));
  height:13%;gap:calc(var(--page-w) * .01);margin-top:calc(var(--page-w) * .015);
}
body[data-format] .page.growth .growth-thumbs .secondary-photo{
  width:100%;height:100%;min-height:0;aspect-ratio:auto;border-radius:0;
}
body[data-format] .page.growth .timeline{
  --growth-entry-title:.021;
  --growth-entry-copy:.0125;
  position:relative;min-width:0;min-height:0;padding:0;
  display:grid;grid-auto-rows:minmax(0,1fr);gap:3%;
  align-content:start;
}
body[data-format] .page.growth .timeline::before{display:none}
body[data-format] .page.growth .timeline article{
  position:relative;display:grid;grid-template-columns:calc(var(--page-w) * .033) minmax(0,1fr);
  column-gap:calc(var(--page-w) * .016);align-content:start;
  min-width:0;min-height:0;max-height:none;margin:0;
  padding-top:calc(var(--page-w) * .014);
  border-top:1px solid var(--quiet-rule);
}
body[data-format] .page.growth .timeline article>span{
  display:block;position:static;
  width:auto;height:auto;border-radius:0;background:none;color:#819078;
  font-family:'Bodoni Moda',serif;font-size:calc(var(--page-w) * .034);
  font-weight:500;line-height:1.06;font-variant-numeric:lining-nums;
}
body[data-format] .page.growth .timeline article>div{min-width:0}
body[data-format] .page.growth .timeline h2{
  margin:0 0 calc(var(--page-w) * .008);
  font-family:'Noto Serif JP Variable',serif;
  font-size:calc(var(--page-w) * var(--growth-entry-title));font-weight:500;
  line-height:1.65;letter-spacing:.025em;overflow-wrap:anywhere;
}
body[data-format] .page.growth .timeline p{
  margin:0;font-size:calc(var(--page-w) * var(--growth-entry-copy));line-height:1.9;
  letter-spacing:.01em;overflow-wrap:anywhere;
}
body[data-format] .page.growth .timeline:has(article:nth-child(5)){
  --growth-entry-title:.0145;--growth-entry-copy:.009;
  grid-template-columns:repeat(2,minmax(0,1fr));column-gap:8%;row-gap:3%;
}
body[data-format] .page.growth .timeline:has(article:nth-child(5)) article{
  display:block;padding-top:calc(var(--page-w) * .011);
}
body[data-format] .page.growth .timeline:has(article:nth-child(5)) article>span{
  margin-bottom:calc(var(--page-w) * .012);font-size:calc(var(--page-w) * .027);
}
body[data-format] .page.growth>footer{
  position:absolute;left:6.5%;top:85%;bottom:6.4%;width:37.4%;max-width:none;margin:0;
  padding-top:calc(var(--page-w) * .012);border-top:1px solid var(--quiet-rule);
  font-family:'Noto Serif JP Variable',serif;
  font-size:calc(var(--page-w) * .0088);font-weight:400;line-height:1.75;
  letter-spacing:.02em;overflow-wrap:anywhere;
}
body[data-format] .page.growth:has(>footer) .growth-thumbs{height:10%}
body[data-format] .page.growth:has(>footer) .growth-photo{height:72%}

body[data-format='sns_4x5_jpeg'] .page.growth .growth-grid{top:25.5%;bottom:8%;grid-template-columns:41% minmax(0,1fr);gap:6%}
body[data-format='sns_4x5_jpeg'] .page.growth .timeline{--growth-entry-title:.019;--growth-entry-copy:.011}
body[data-format='sns_4x5_jpeg'] .page.growth .timeline:has(article:nth-child(5)){--growth-entry-title:.0125;--growth-entry-copy:.008}
body[data-format='sns_4x5_jpeg'] .page.growth>footer{width:35.7%;font-size:calc(var(--page-w) * .0082)}

/* Short memories earn a larger reading size; longer cover summaries retain their full allocation. */
body[data-format] .page.storybook[data-story-density='short'] .story-copy h2{
  font-size:calc(var(--page-w) * .026);line-height:1.6;
}
body[data-format] .page.storybook[data-story-density='short'] .story-copy p{
  font-size:calc(var(--page-w) * .017);line-height:1.95;
}
body[data-format] .page.storybook[data-story-density='short'] .story-copy blockquote{
  font-size:calc(var(--page-w) * .013);line-height:1.9;
}
body[data-format='two_l_jpeg'] .page.storybook[data-story-density='short'] .story-copy h2{
  font-size:calc(var(--page-w) * .019);
}
body[data-format='two_l_jpeg'] .page.storybook[data-story-density='short'] .story-copy p{
  font-size:calc(var(--page-w) * .0115);line-height:1.9;
}
body[data-format='two_l_jpeg'] .page.storybook[data-story-density='short'] .story-copy blockquote{
  font-size:calc(var(--page-w) * .0095);line-height:1.85;
}
body[data-format] .page.growth[data-growth-density='short'] .timeline{
  --growth-entry-title:.024;--growth-entry-copy:.016;
}
body[data-format] .page.growth[data-growth-density='short'] .timeline:has(article:nth-child(5)){
  --growth-entry-title:.018;--growth-entry-copy:.012;
}
body[data-format] .page.growth[data-growth-density='short'] .timeline:has(article:nth-child(5)) article{
  padding-top:calc(var(--page-w) * .009);
}
body[data-format] .page.growth[data-growth-density='short'] .timeline:has(article:nth-child(5)) article>span{
  font-size:calc(var(--page-w) * .034);line-height:1;
  margin-bottom:calc(var(--page-w) * .006);
}
body[data-format] .page.growth[data-growth-density='short'] .timeline:has(article:nth-child(5)) h2{
  line-height:1.45;margin-bottom:calc(var(--page-w) * .005);
}
body[data-format] .page.growth[data-growth-density='short'] .timeline:has(article:nth-child(5)) p{
  line-height:1.65;
}
body[data-format='sns_4x5_jpeg'] .page.growth[data-growth-density='short'] .timeline:not(:has(article:nth-child(5))){
  --growth-entry-title:.023;--growth-entry-copy:.015;
}
`;
